<template>
  <div class="wrapper">
    <div class="header">
      <div class="left-part">
        <div class="logo">
          <img src="https://cdn.jirou.fit/tos-cn-i-700tmnc25s/d7e19ab1966d4f728489ed42f1a2551c~tplv-700tmnc25s-yasuo.image" class="logo-img" alt="logo" />
          <span class="logo-text">中国健美赛事管理系统</span>
        </div>
        <el-button
          v-if="isMobile"
          class="mobile-menu-button"
          icon="el-icon-menu"
          circle
          size="mini"
          @click="mobileMenuVisible = true"
        ></el-button>
      </div>
      <div class="right-part">
        <span class="token-timer" :class="{ 'token-warning': tokenExpiringSoon }">
          <i class="el-icon-time"></i>
          {{ tokenRemainingText }}
        </span>
        <el-popover
          placement="bottom-end"
          width="220"
          trigger="click"
          popper-class="theme-color-popover"
        >
          <div class="theme-color-panel">
            <div class="theme-color-panel__title">主题色</div>
            <el-color-picker
              v-model="brandColor"
              size="small"
              :predefine="predefineColors"
              @change="handleBrandColorChange"
            ></el-color-picker>
            <el-button size="mini" type="text" @click="resetBrandColor">恢复默认</el-button>
          </div>
          <button
            slot="reference"
            type="button"
            class="theme-color-trigger"
            title="自定义主题色"
            :style="{ backgroundColor: brandColor }"
          ></button>
        </el-popover>
        <theme-toggle @theme-changed="onThemeChanged"></theme-toggle>
        <el-link type="primary" @click="logout">注销</el-link>
      </div>
    </div>
    <el-drawer
      :visible.sync="mobileMenuVisible"
      direction="ltr"
      size="82%"
      :with-header="false"
      custom-class="mobile-nav-drawer"
    >
      <div class="mobile-drawer">
        <div class="mobile-drawer-header">
          <div>
            <div class="mobile-drawer-title">菜单</div>
            <div class="mobile-drawer-subtitle">长按菜单项可在新标签页打开</div>
          </div>
          <el-button icon="el-icon-close" circle size="mini" @click="mobileMenuVisible = false"></el-button>
        </div>
        <div
          v-for="group in mobileMenuGroups"
          :key="group.title"
          class="mobile-menu-group"
        >
          <div class="mobile-menu-group-title">
            <i :class="group.icon"></i>
            <span>{{ group.title }}</span>
          </div>
          <a
            v-for="item in group.items"
            :key="item.index"
            class="mobile-menu-link"
            :class="{ active: item.index === activeIndex }"
            :href="getMenuHref(item.index)"
            @click.prevent="navigateMenu(item.index)"
          >
            <span>{{ item.label }}</span>
            <i class="el-icon-arrow-right"></i>
          </a>
        </div>
      </div>
    </el-drawer>
    <div class="app-body">
      <aside v-if="!isMobile" class="sidebar">
        <el-menu
          :default-active="activeIndex"
          :default-openeds="openedMenuGroups"
          class="side-menu"
          :background-color="menuBgColor"
          :text-color="menuTextColor"
          :active-text-color="menuActiveColor"
          @select="handleSelect"
        >
          <el-submenu
            v-for="group in pcMenuGroups"
            :key="group.index"
            :index="group.index"
          >
            <template slot="title">
              <span class="side-menu-title">
                <i :class="group.icon"></i>
                <span>{{ group.title }}</span>
              </span>
            </template>
            <template v-for="item in group.items">
              <el-submenu
                v-if="item.children"
                :key="item.index"
                :index="item.index"
              >
                <template slot="title">{{ item.label }}</template>
                <el-menu-item
                  v-for="child in item.children"
                  :key="child.index"
                  :index="child.index"
                >
                  <a
                    class="menu-link"
                    :href="getMenuHref(child.index)"
                    @click="handleMenuLinkClick($event, child.index)"
                  >{{ child.label }}</a>
                </el-menu-item>
              </el-submenu>
              <el-menu-item
                v-else
                :key="item.index"
                :index="item.index"
              >
                <a
                  class="menu-link"
                  :href="getMenuHref(item.index)"
                  @click="handleMenuLinkClick($event, item.index)"
                >{{ item.label }}</a>
              </el-menu-item>
            </template>
          </el-submenu>
        </el-menu>
      </aside>
      <el-card class="main" shadow="hover">
        <div v-if="showPageTitle" class="page-header-title">
          <h1>{{ currentPageTitle }}</h1>
        </div>
        <router-view></router-view>
      </el-card>
    </div>
  </div>
</template>

<script>
import ThemeToggle from '@/components/ThemeToggle.vue'
import ThemeManager from '@/utils/theme.js'
import { clearAuth, hasPermission } from '@/utils/auth.js'

const routerMap = {
  eventManage: {
    path: "/manage",
    label: "赛事列表",
  },
  addEvent: {
    path: "/add",
    label: "添加赛事",
  },
  noticeManage: {
    path: "/notice",
    label: "通知管理",
  },
  adsManage: {
    path: "/ads",
    label: "推荐位管理",
  },
  database: {
    path: "/database",
    label: "历史赛事数据库",
  },
  formResult: {
    path: "/form_result",
    label: "报名名单管理",
  },
  registrationAddon: {
    path: "/registration-addons",
    label: "兼项管理",
  },
  registrationDailyDashboard: {
    path: "/registration-dashboard/daily",
    label: "报名数据大盘",
  },
  registrationMonthlyDashboard: {
    path: "/registration-dashboard/monthly",
    label: "月数据大盘",
  },
  registrationMapDashboard: {
    path: "/registration-dashboard/map",
    label: "地图大盘",
  },
  registrationBrandDashboard: {
    path: "/registration-dashboard/brand",
    label: "品牌数据大盘",
  },
  couponCode: {
    path: "/coupon-code",
    label: "优惠码管理",
  },
  liveEvent: {
    path: "/live-event",
    label: "直播管理",
  },
  obsOverlay: {
    path: "/obs-overlay",
    label: "OBS 直播画面",
  },
  equipmentManage: {
    path: "/equipment",
    label: "商品管理",
  },
  addArticle: {
    path: "/add-article",
    label: "添加文章",
  },
  articleList: {
    path: "/article-list",
    label: "文章列表",
  },
  imageUpload: {
    path: "/image-upload",
    label: "图片上传",
  },
  moreTools: {
    externalUrl: "https://www.musclegame.cn/tool/",
    label: "更多实用工具",
  },
  userManage: {
    path: "/users",
    label: "用户管理",
  },
  roleManage: {
    path: "/roles",
    label: "角色管理",
  },
  operationLog: {
    path: "/operation-logs",
    label: "操作日志",
  }
};

// 路径到菜单 index 的反向映射
const pathToIndexMap = {
  '/manage': 'eventManage',
  '/add': 'addEvent',
  '/notice': 'noticeManage',
  '/ads': 'adsManage',
  '/database': 'database',
  '/form_result': 'formResult',
  '/registration-addons': 'registrationAddon',
  '/registration-dashboard': 'registrationDailyDashboard',
  '/registration-dashboard/daily': 'registrationDailyDashboard',
  '/registration-dashboard/monthly': 'registrationMonthlyDashboard',
  '/registration-dashboard/map': 'registrationMapDashboard',
  '/registration-dashboard/brand': 'registrationBrandDashboard',
  '/coupon-code': 'couponCode',
  '/live-event': 'liveEvent',
  '/obs-overlay': 'obsOverlay',
  '/equipment': 'equipmentManage',
  '/add-article': 'addArticle',
  '/article-list': 'articleList',
  '/image-upload': 'imageUpload',
  '/users': 'userManage',
  '/roles': 'roleManage',
  '/operation-logs': 'operationLog'
};

const mobileMenuGroups = [
  {
    title: '赛事管理',
    icon: 'el-icon-trophy',
    items: [
      { index: 'eventManage', label: '赛事列表', permission: 'event:view' },
      { index: 'addEvent', label: '新增赛事', permission: 'event:create' },
      { index: 'database', label: '历史赛事数据库', permission: 'event:view' }
    ]
  },
  {
    title: '报名管理',
    icon: 'el-icon-s-order',
    items: [
      { index: 'formResult', label: '报名名单管理', permission: 'registration:view' },
      { index: 'registrationAddon', label: '兼项管理', permission: 'registration:addon:view' },
      { index: 'couponCode', label: '优惠码管理', permission: 'coupon:view' }
    ]
  },
  {
    title: '报名数据大盘',
    icon: 'el-icon-data-analysis',
    items: [
      { index: 'registrationDailyDashboard', label: '日数据大盘', permission: 'dashboard:daily:view' },
      { index: 'registrationMonthlyDashboard', label: '月数据大盘', permission: 'dashboard:monthly:view' },
      { index: 'registrationMapDashboard', label: '地图大盘', permission: 'dashboard:map:view' },
      { index: 'registrationBrandDashboard', label: '品牌大盘', permission: 'dashboard:brand:view' }
    ]
  },
  {
    title: '配置管理',
    icon: 'el-icon-setting',
    items: [
      { index: 'noticeManage', label: '通知管理', permission: 'content:notice' },
      { index: 'adsManage', label: '推荐位管理', permission: 'content:ads' },
      { index: 'liveEvent', label: '直播管理', permission: 'content:live' },
      { index: 'obsOverlay', label: 'OBS 直播画面', permission: 'content:obsOverlay' },
      { index: 'equipmentManage', label: '商品管理', permission: 'content:equipment' },
      { index: 'articleList', label: '文章列表', permission: 'content:article' },
      { index: 'addArticle', label: '添加文章', permission: 'content:article' }
    ]
  },
  {
    title: '工具',
    icon: 'el-icon-folder-opened',
    items: [
      { index: 'imageUpload', label: '图片上传', permission: 'upload:image' },
      { index: 'moreTools', label: '更多实用工具', permission: 'tool:external:view' }
    ]
  },
  {
    title: '系统管理',
    icon: 'el-icon-setting',
    items: [
      { index: 'userManage', label: '用户管理', permission: 'system:user:view' },
      { index: 'roleManage', label: '角色管理', permission: 'system:role:view' },
      { index: 'operationLog', label: '操作日志', permission: '*' }
    ]
  }
];

const pcMenuGroups = [
  {
    index: 'eventGroup',
    title: '赛事管理',
    icon: 'el-icon-trophy',
    items: [
      { index: 'eventManage', label: '赛事列表', permission: 'event:view' },
      { index: 'addEvent', label: '新增赛事', permission: 'event:create' },
      { index: 'database', label: '历史赛事数据库', permission: 'event:view' }
    ]
  },
  {
    index: 'registrationManageGroup',
    title: '报名管理',
    icon: 'el-icon-s-order',
    items: [
      { index: 'formResult', label: '报名名单管理', permission: 'registration:view' },
      { index: 'registrationAddon', label: '兼项管理', permission: 'registration:addon:view' },
      { index: 'couponCode', label: '优惠码管理', permission: 'coupon:view' }
    ]
  },
  {
    index: 'configGroup',
    title: '配置管理',
    icon: 'el-icon-setting',
    items: [
      { index: 'noticeManage', label: '通知管理', permission: 'content:notice' },
      { index: 'adsManage', label: '推荐位管理', permission: 'content:ads' },
      { index: 'liveEvent', label: '直播管理', permission: 'content:live' },
      { index: 'obsOverlay', label: 'OBS 直播画面', permission: 'content:obsOverlay' },
      { index: 'equipmentManage', label: '商品管理', permission: 'content:equipment' },
      {
        index: 'articleManage',
        label: '文章管理',
        children: [
          { index: 'articleList', label: '文章列表', permission: 'content:article' },
          { index: 'addArticle', label: '添加文章', permission: 'content:article' }
        ]
      }
    ]
  },
  {
    index: 'registrationDashboardGroup',
    title: '报名数据大盘',
    icon: 'el-icon-data-analysis',
    items: [
      { index: 'registrationDailyDashboard', label: '日数据大盘', permission: 'dashboard:daily:view' },
      { index: 'registrationMonthlyDashboard', label: '月数据大盘', permission: 'dashboard:monthly:view' },
      { index: 'registrationMapDashboard', label: '地图大盘', permission: 'dashboard:map:view' },
      { index: 'registrationBrandDashboard', label: '品牌大盘', permission: 'dashboard:brand:view' }
    ]
  },
  {
    index: 'toolGroup',
    title: '工具',
    icon: 'el-icon-folder-opened',
    items: [
      { index: 'imageUpload', label: '图片上传', permission: 'upload:image' },
      { index: 'moreTools', label: '更多实用工具', permission: 'tool:external:view' }
    ]
  },
  {
    index: 'systemGroup',
    title: '系统管理',
    icon: 'el-icon-setting',
    items: [
      { index: 'userManage', label: '用户管理', permission: 'system:user:view' },
      { index: 'roleManage', label: '角色管理', permission: 'system:role:view' },
      { index: 'operationLog', label: '操作日志', permission: '*' }
    ]
  }
];

const openedMenuGroups = pcMenuGroups.map(group => group.index).concat('articleManage');

const filterMenuItems = (items) => {
  return items.reduce((list, item) => {
    if (item.permission && !hasPermission(item.permission)) {
      return list;
    }
    if (item.children) {
      const children = filterMenuItems(item.children);
      if (children.length > 0) {
        list.push({
          ...item,
          children
        });
      }
      return list;
    }
    list.push(item);
    return list;
  }, []);
};

const filterMenuGroups = (groups) => {
  return groups.map(group => ({
    ...group,
    items: filterMenuItems(group.items)
  })).filter(group => group.items.length > 0);
};

export default {
  name: "Index",
  components: {
    ThemeToggle
  },
  data() {
    return {
      activeIndex: "eventManage",
      isDarkMode: false,
      brandColor: ThemeManager.getBrandColor(),
      predefineColors: [
        '#c04851',
        '#409eff',
        '#2f80ed',
        '#16a34a',
        '#7c3aed',
        '#e6a23c',
        '#f56c6c',
        '#111827'
      ],
      tokenRemaining: 0,
      tokenTimer: null,
      isMobile: false,
      mobileMenuVisible: false,
      mobileMenuGroups: filterMenuGroups(mobileMenuGroups),
      pcMenuGroups: filterMenuGroups(pcMenuGroups),
      openedMenuGroups
    };
  },
  computed: {
    menuBgColor() {
      return this.isDarkMode ? '#161b22' : '#ffffff';
    },
    menuTextColor() {
      return this.isDarkMode ? '#c9d1d9' : '#303133';
    },
    menuActiveColor() {
      return this.brandColor;
    },
    tokenRemainingText() {
      if (this.tokenRemaining <= 0) return '已过期';
      const h = Math.floor(this.tokenRemaining / 3600);
      const m = Math.floor((this.tokenRemaining % 3600) / 60);
      const s = this.tokenRemaining % 60;
      if (h > 0) {
        return h + '时' + String(m).padStart(2, '0') + '分' + String(s).padStart(2, '0') + '秒';
      }
      if (m > 0) {
        return m + '分' + String(s).padStart(2, '0') + '秒';
      }
      return s + '秒';
    },
    tokenExpiringSoon() {
      return this.tokenRemaining > 0 && this.tokenRemaining <= 3600;
    },
    currentPageTitle() {
      if (this.$route.meta && this.$route.meta.title) {
        return this.$route.meta.title;
      }
      const routeConfig = routerMap[this.activeIndex];
      return routeConfig ? routeConfig.label : "";
    },
    showPageTitle() {
      return Boolean(this.currentPageTitle);
    }
  },
  created() {
    this.isDarkMode = ThemeManager.isDark();
    this.unsubscribe = ThemeManager.subscribe((isDark, brandColor) => {
      this.isDarkMode = isDark;
      this.brandColor = brandColor;
    });
    this.startTokenTimer();
    // 根据当前路由设置菜单高亮
    this.updateActiveIndex(this.$route.path);
    this.updateDocumentTitle();
  },
  mounted() {
    this.updateViewport();
    window.addEventListener('resize', this.updateViewport);
    window.addEventListener('bde-auth-updated', this.refreshPermissionMenus);
  },
  watch: {
    $route(to) {
      this.updateActiveIndex(to.path);
      this.updateDocumentTitle();
    }
  },
  beforeDestroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    if (this.tokenTimer) {
      clearInterval(this.tokenTimer);
    }
    window.removeEventListener('resize', this.updateViewport);
    window.removeEventListener('bde-auth-updated', this.refreshPermissionMenus);
  },
  methods: {
    refreshPermissionMenus() {
      this.mobileMenuGroups = filterMenuGroups(mobileMenuGroups);
      this.pcMenuGroups = filterMenuGroups(pcMenuGroups);
    },
    updateViewport() {
      this.isMobile = window.innerWidth <= 768;
    },
    updateActiveIndex(path) {
      // 忽略查询参数，只匹配路径
      const basePath = path.split('?')[0];
      const index = pathToIndexMap[basePath];
      if (index) {
        this.activeIndex = index;
      }
    },
    updateDocumentTitle() {
      const title = this.$route.meta && this.$route.meta.title;
      document.title = title ? `${title} - 中国健美赛事管理系统` : '中国健美赛事管理系统';
    },
    getMenuHref(index) {
      const routeConfig = routerMap[index];
      if (!routeConfig) return '#';
      if (routeConfig.externalUrl) return routeConfig.externalUrl;
      return this.$router.resolve({
        path: routeConfig.path,
      }).href;
    },
    handleSelect(index) {
      this.navigateMenu(index);
    },
    handleMenuLinkClick(event, index) {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      event.preventDefault();
      this.navigateMenu(index);
    },
    navigateMenu(index) {
      const routeConfig = routerMap[index];
      if (!routeConfig) return;

      this.mobileMenuVisible = false;
      if (routeConfig.externalUrl) {
        window.open(routeConfig.externalUrl, '_blank', 'noopener');
        return;
      }
      if (this.$route.path === routeConfig.path) return;
      this.$router.push({
        path: routeConfig.path,
      }).catch(() => {});
    },
    logout() {
      clearAuth();
      this.$router.push("/login");
    },
    onThemeChanged(isDark) {
      this.isDarkMode = isDark;
    },
    handleBrandColorChange(color) {
      if (!color) return;
      this.brandColor = ThemeManager.setBrandColor(color);
    },
    resetBrandColor() {
      this.brandColor = ThemeManager.resetBrandColor();
    },
    startTokenTimer() {
      try {
        const token = localStorage.getItem('bde_token');
        if (!token) return;
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (!payload.exp) return;
        this.tokenRemaining = payload.exp - Math.floor(Date.now() / 1000);
        if (this.tokenRemaining <= 0) {
          this.tokenRemaining = 0;
          return;
        }
        this.tokenTimer = setInterval(() => {
          this.tokenRemaining--;
          if (this.tokenRemaining <= 0) {
            this.tokenRemaining = 0;
            clearInterval(this.tokenTimer);
          }
        }, 1000);
      } catch (e) {
        console.error('Token解析失败:', e);
      }
    }
  },
};
</script>

<style lang="css" scoped>
.wrapper {
  --layout-x-padding: 24px;
  width: 100%;
  min-height: 100vh;
  margin-top: 0;
  background-color: var(--bg-page);
  transition: background-color 0.3s ease;
}

.header {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 60px;
  padding: 0 var(--layout-x-padding);
  background-color: var(--bg-header);
  border-bottom: 1px solid var(--border-lighter);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.logo {
  display: flex;
  align-items: center;
}

.left-part {
  display: flex;
  align-items: center;
  gap: 10px;
}

.logo-img {
  height: 36px;
  margin-right: 10px;
}

.logo-text {
  color: var(--brand-primary);
  font-weight: bold;
  font-size: 20px;
}

.right-part {
  display: flex;
  align-items: center;
  gap: 16px;
}

.token-timer {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: var(--text-secondary, #909399);
  gap: 4px;
}

.token-timer.token-warning {
  color: #E6A23C;
}

.theme-color-trigger {
  width: 24px;
  height: 24px;
  padding: 0;
  border: 2px solid var(--bg-card);
  border-radius: 50%;
  box-shadow: 0 0 0 1px var(--border-base);
  cursor: pointer;
}

.main {
  box-sizing: border-box;
  flex: 1;
  min-width: 0;
  padding: 10px;
  font-size: 20px;
}

.app-body {
  box-sizing: border-box;
  display: flex;
  gap: 20px;
  width: 100%;
  min-height: calc(100vh - 60px);
  padding: 24px var(--layout-x-padding);
}

.sidebar {
  flex: 0 0 240px;
  min-width: 0;
}

.side-menu {
  border: 1px solid var(--border-lighter);
  border-radius: 6px;
  overflow: hidden;
}

.side-menu:not(.el-menu--collapse) {
  width: 240px;
}

.side-menu >>> .el-submenu__title,
.side-menu >>> .el-menu-item {
  height: 44px;
  line-height: 44px;
}

.side-menu >>> .el-submenu__title {
  padding: 0 16px !important;
}

.side-menu >>> .el-menu-item {
  padding: 0 16px 0 48px !important;
}

.side-menu >>> .el-menu .el-submenu__title {
  padding: 0 16px 0 48px !important;
}

.side-menu >>> .el-menu .el-submenu .el-menu-item {
  padding: 0 16px 0 64px !important;
}

.side-menu-title {
  display: inline-grid;
  grid-template-columns: 24px 1fr;
  align-items: center;
  column-gap: 8px;
  width: 100%;
}

.side-menu-title i {
  width: 24px;
  margin-right: 0;
  text-align: center;
}

.side-menu >>> .el-submenu__title:hover,
.side-menu >>> .el-menu-item:hover {
  background-color: var(--brand-primary-lighter) !important;
  color: var(--brand-primary) !important;
}

.side-menu >>> .el-menu-item.is-active {
  background-color: var(--brand-primary-lighter) !important;
  color: var(--brand-primary) !important;
  font-weight: 700;
}

.dark-mode .side-menu >>> .el-submenu__title:hover,
.dark-mode .side-menu >>> .el-menu-item:hover,
.dark-mode .side-menu >>> .el-menu-item.is-active {
  background-color: rgba(var(--brand-primary-rgb), 0.16) !important;
}

.page-header-title {
  margin-bottom: 18px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--border-lighter);
}

.page-header-title h1 {
  margin: 0;
  color: var(--text-primary);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.menu-link {
  display: inline-flex;
  align-items: center;
  height: 100%;
  color: inherit;
  text-decoration: none;
}

.mobile-menu-button {
  display: none;
}

.mobile-drawer {
  box-sizing: border-box;
  min-height: 100%;
  padding: 18px 16px calc(18px + env(safe-area-inset-bottom));
  background: var(--bg-page);
}

.mobile-drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
}

.mobile-drawer-title {
  color: var(--text-primary);
  font-size: 20px;
  font-weight: 700;
}

.mobile-drawer-subtitle {
  margin-top: 4px;
  color: var(--text-secondary);
  font-size: 12px;
}

.mobile-menu-group {
  margin-bottom: 18px;
}

.mobile-menu-group-title {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  color: var(--brand-primary);
  font-size: 12px;
  font-weight: 700;
}

.mobile-menu-group-title i {
  width: 16px;
  text-align: center;
}

.mobile-menu-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 44px;
  margin-bottom: 8px;
  border: 1px solid var(--border-lighter);
  border-radius: 8px;
  background: var(--bg-card);
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  padding: 0 12px;
  text-decoration: none;
}

.mobile-menu-link:hover {
  border-color: var(--brand-primary);
  color: var(--brand-primary);
  background: var(--brand-primary-lighter);
}

.mobile-menu-link.active {
  border-color: var(--brand-primary);
  color: var(--brand-primary);
  background: var(--brand-primary-lighter);
}

.dark-mode .mobile-menu-link.active {
  border-color: rgba(var(--brand-primary-rgb), 0.9);
  color: var(--brand-primary-light);
  background: rgba(var(--brand-primary-rgb), 0.16);
  box-shadow: inset 0 0 0 1px rgba(var(--brand-primary-rgb), 0.18);
}

.mobile-menu-link i {
  color: var(--text-secondary);
  font-size: 13px;
}

.mobile-menu-link:hover i,
.mobile-menu-link.active i,
.dark-mode .mobile-menu-link.active i {
  color: var(--brand-primary);
}

.dark-mode .mobile-menu-link:hover {
  border-color: rgba(var(--brand-primary-rgb), 0.9);
  color: var(--brand-primary-light);
  background: rgba(var(--brand-primary-rgb), 0.16);
}

.dark-mode .mobile-menu-link:hover i,
.dark-mode .mobile-menu-link.active i {
  color: var(--brand-primary-light);
}

.wrapper >>> .mobile-nav-drawer {
  overflow: auto;
  background: var(--bg-page);
}

.wrapper >>> .mobile-nav-drawer .el-drawer__body {
  min-height: 100%;
  overflow: visible;
}

@media (max-width: 768px) {
  .wrapper {
    --layout-x-padding: 12px;
  }

  .header {
    height: 52px;
  }

  .logo-img {
    height: 30px;
    margin-right: 0;
  }

  .logo-text {
    display: none;
  }

  .mobile-menu-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-color: var(--border-lighter);
    color: var(--brand-primary);
  }

  .right-part {
    gap: 8px;
  }

  .token-timer {
    max-width: 116px;
    font-size: 12px;
    white-space: nowrap;
  }

  .main {
    width: 100%;
    margin-top: 12px;
    border-left: 0;
    border-right: 0;
    border-radius: 0;
    padding: 0;
    font-size: 14px;
  }

  .app-body {
    display: block;
    min-height: calc(100vh - 52px);
    padding: 0;
  }

  .page-header-title {
    margin-bottom: 14px;
    padding-bottom: 10px;
  }

  .page-header-title h1 {
    font-size: 17px;
  }
}
</style>

<style>
.theme-color-popover {
  min-width: 220px;
}

.theme-color-panel {
  display: flex;
  align-items: center;
  gap: 12px;
}

.theme-color-panel__title {
  color: var(--text-primary, #303133);
  font-size: 13px;
  font-weight: 700;
}
</style>
